import { apiService } from './api';

export interface SocialSignal {
  id: string;
  weekId: string;
  text: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface PrivateBehavior {
  id: string;
  weekId: string;
  text: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface ValuedGoal {
  id: string;
  weekId: string;
  text: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RadicallyOpenSkill {
  id: string;
  name: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface WeekRadicallyOpenSkill {
  id: string;
  weekId: string;
  skillId: string;
  selectedAt: string;
  skill?: RadicallyOpenSkill;
}

export interface DailyRadicallyOpenSkillRating {
  id: string;
  userId: string;
  weekRadicallyOpenSkillId: string;
  date: string;
  completed: boolean;
  createdAt: string;
  updatedAt: string;
  weekRadicallyOpenSkill?: WeekRadicallyOpenSkill;
}

export enum RiskFactorType {
  NUMERIC = 'numeric',
  BOOLEAN = 'boolean',
  TEXT = 'text',
}

export interface RiskFactor {
  id: string;
  name: string;
  displayName: string;
  description?: string | null;
  type: RiskFactorType;
  sortOrder?: number;
  detailPlaceholder?: string | null;
}

export interface DailyRiskFactorRating {
  id: string;
  userId: string;
  riskFactorId: string;
  date: string;
  numericValue?: number | null;
  booleanValue?: boolean | null;
  textValue?: string | null;
  riskFactor?: RiskFactor;
  createdAt: string;
  updatedAt: string;
}

export interface WeekSelfEnquiryQuestion {
  id: string;
  weekId?: string;
  text: string;
  sortOrder: number;
  kind: 'custom' | 'preset';
  presetKey: string | null;
}

export interface PresetSelfEnquiryQuestion {
  key: string;
  text: string;
}

export interface SelfEnquiryPresetGroup {
  id: string;
  title: string;
  questions: PresetSelfEnquiryQuestion[];
}

export interface SelfEnquiryPresetCatalog {
  groups: SelfEnquiryPresetGroup[];
}

export interface DailySelfEnquiryView {
  weekCustomQuestions: Array<{ id: string; text: string; sortOrder: number }>;
  weekPresetQuestions: Array<{ id: string; text: string; presetKey: string | null; sortOrder: number }>;
  dayExtraQuestions: Array<{ id: string; text: string; presetKey: string | null; sortOrder: number }>;
  answerText: string;
}

/** Запись дневного трекинга самоисследования (журнал). */
export interface SelfEnquiryJournalEntry {
  id: string;
  weekId: string;
  date: string;
  questionSummary: string;
  questions: string[];
  answerText: string;
}

export interface Week {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  createdAt: string;
  notes?: string | null;
  weekSkills?: WeekSkill[];
  socialSignals?: SocialSignal[];
  privateBehaviors?: PrivateBehavior[];
  valuedGoal?: ValuedGoal | null;
  weekRadicallyOpenSkills?: WeekRadicallyOpenSkill[];
  weekSelfEnquiryQuestions?: WeekSelfEnquiryQuestion[];
}

export interface WeekSkill {
  id: string;
  weekId: string;
  skillId: string;
  selectedAt: string;
  skill?: Skill;
}

export interface Skill {
  id: string;
  name: string;
  category: 'activate' | 'awareness';
  description: string;
}

export interface DailyRating {
  id: string;
  socialSignalId: string;
  socialSignalText: string;
  date: string;
  rating: number;
  createdAt: string;
  updatedAt: string;
}

export interface BulkUpdateDailyRatingsDto {
  date: string;
  ratings: Array<{ socialSignalId: string; rating: number }>;
}

export interface BulkUpdateDailyPrivateBehaviorRatingsDto {
  date: string;
  ratings: Array<{ privateBehaviorId: string; rating: number }>;
}

export interface CreateWeekDto {
  startDate: string;
  endDate: string;
  isCurrent?: boolean;
  confirmOverlap?: boolean;
}

export interface UpdateWeekDto {
  startDate?: string;
  endDate?: string;
  isCurrent?: boolean;
  notes?: string;
  confirmOverlap?: boolean;
}

export interface ArchiveReviewPayload {
  week: { id: string; startDate: string; endDate: string };
  days: string[];
  notes: string | null;
  valuedGoal: { text: string | null } | null;
  riskFactors: Array<{
    id: string;
    name: string;
    displayName: string;
    type: RiskFactorType;
    values: Array<number | boolean | string | null>;
  }>;
  socialSignals: Array<{ id: string; text: string; values: Array<number | null> }>;
  privateBehaviors: Array<{ id: string; text: string; values: Array<number | null> }>;
  roSkills: Array<{ id: string; skillName: string; values: Array<boolean | null> }>;
  selfEnquiry: Array<{ date: string; answerText: string }>;
}

export const weeksService = {
  create: async (data: CreateWeekDto): Promise<Week> => {
    return apiService.client.post('/api/weeks', data);
  },

  listAll: async (): Promise<Week[]> => {
    return apiService.client.get('/api/weeks');
  },

  getTrackingJournalDates: async (): Promise<string[]> => {
    return apiService.client.get('/api/weeks/tracking-journal-dates');
  },

  getCurrent: async (): Promise<Week | null> => {
    return apiService.client.get('/api/weeks/current');
  },

  getById: async (id: string): Promise<Week> => {
    return apiService.client.get(`/api/weeks/${id}`);
  },

  update: async (id: string, data: UpdateWeekDto): Promise<Week> => {
    return apiService.client.put(`/api/weeks/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiService.client.delete(`/api/weeks/${id}`);
  },

  selectSkills: async (weekId: string, skillIds: string[]): Promise<WeekSkill[]> => {
    return apiService.client.post(`/api/weeks/${weekId}/skills`, { skillIds });
  },

  getSelectedSkills: async (weekId: string): Promise<WeekSkill[]> => {
    return apiService.client.get(`/api/weeks/${weekId}/skills`);
  },

  removeSkill: async (weekId: string, skillId: string): Promise<void> => {
    return apiService.client.delete(`/api/weeks/${weekId}/skills/${skillId}`);
  },

  updateSocialSignals: async (weekId: string, signals: { text: string }[]): Promise<SocialSignal[]> => {
    return apiService.client.put(`/api/weeks/${weekId}/social-signals`, { signals });
  },

  getSocialSignals: async (weekId: string): Promise<SocialSignal[]> => {
    return apiService.client.get(`/api/weeks/${weekId}/social-signals`);
  },

  getPreviousWeek: async (weekId: string): Promise<Week | null> => {
    return apiService.client.get(`/api/weeks/${weekId}/previous-week`);
  },

  saveDailyRatings: async (weekId: string, data: BulkUpdateDailyRatingsDto): Promise<DailyRating[]> => {
    return apiService.client.post(`/api/weeks/${weekId}/daily-ratings`, data);
  },

  getDailyRatings: async (weekId: string, date: string): Promise<DailyRating[]> => {
    return apiService.client.get(`/api/weeks/${weekId}/daily-ratings/${date}`);
  },

  updatePrivateBehaviors: async (weekId: string, behaviors: { text: string }[]): Promise<PrivateBehavior[]> => {
    return apiService.client.put(`/api/weeks/${weekId}/private-behaviors`, { behaviors });
  },

  getPrivateBehaviors: async (weekId: string): Promise<PrivateBehavior[]> => {
    return apiService.client.get(`/api/weeks/${weekId}/private-behaviors`);
  },

  saveDailyPrivateBehaviorRatings: async (weekId: string, data: BulkUpdateDailyPrivateBehaviorRatingsDto): Promise<DailyRating[]> => {
    return apiService.client.post(`/api/weeks/${weekId}/daily-private-behavior-ratings`, data);
  },

  getDailyPrivateBehaviorRatings: async (weekId: string, date: string): Promise<DailyRating[]> => {
    return apiService.client.get(`/api/weeks/${weekId}/daily-private-behavior-ratings/${date}`);
  },

  updateValuedGoal: async (weekId: string, text: string | null): Promise<ValuedGoal> => {
    return apiService.client.put(`/api/weeks/${weekId}/valued-goal`, { text });
  },

  getValuedGoal: async (weekId: string): Promise<ValuedGoal | null> => {
    return apiService.client.get(`/api/weeks/${weekId}/valued-goal`);
  },

  selectRadicallyOpenSkills: async (weekId: string, skillIds: string[]): Promise<WeekRadicallyOpenSkill[]> => {
    return apiService.client.post(`/api/weeks/${weekId}/radically-open-skills`, { skillIds });
  },

  getSelectedRadicallyOpenSkills: async (weekId: string): Promise<WeekRadicallyOpenSkill[]> => {
    return apiService.client.get(`/api/weeks/${weekId}/radically-open-skills`);
  },

  getAllRadicallyOpenSkills: async (): Promise<RadicallyOpenSkill[]> => {
    return apiService.client.get('/api/weeks/radically-open-skills/all');
  },

  saveDailyRadicallyOpenSkillRatings: async (
    weekId: string,
    data: { date: string; ratings: Array<{ weekRadicallyOpenSkillId: string; completed: boolean }> },
  ): Promise<DailyRadicallyOpenSkillRating[]> => {
    return apiService.client.post(`/api/weeks/${weekId}/daily-radically-open-skill-ratings`, data);
  },

  getDailyRadicallyOpenSkillRatings: async (weekId: string, date: string): Promise<DailyRadicallyOpenSkillRating[]> => {
    return apiService.client.get(`/api/weeks/${weekId}/daily-radically-open-skill-ratings/${date}`);
  },

  findWeekByDate: async (date: string): Promise<Week | null> => {
    return apiService.client.get(`/api/weeks/by-date/${date}`);
  },

  getAllRiskFactors: async (): Promise<RiskFactor[]> => {
    return apiService.client.get('/api/weeks/risk-factors/all');
  },

  saveDailyRiskFactorRatings: async (
    weekId: string,
    data: {
      date: string;
      ratings: Array<{
        riskFactorId: string;
        numericValue?: number;
        booleanValue?: boolean;
        textValue?: string;
      }>;
    },
  ): Promise<DailyRiskFactorRating[]> => {
    return apiService.client.post(`/api/weeks/${weekId}/daily-risk-factor-ratings`, data);
  },

  getDailyRiskFactorRatings: async (weekId: string, date: string): Promise<DailyRiskFactorRating[]> => {
    return apiService.client.get(`/api/weeks/${weekId}/daily-risk-factor-ratings/${date}`);
  },

  getSelfEnquiryPresetQuestions: async (): Promise<SelfEnquiryPresetCatalog> => {
    return apiService.client.get('/api/weeks/self-enquiry-preset-questions');
  },

  getWeekSelfEnquiryQuestions: async (weekId: string): Promise<WeekSelfEnquiryQuestion[]> => {
    return apiService.client.get(`/api/weeks/${weekId}/self-enquiry-questions`);
  },

  updateWeekSelfEnquiryQuestions: async (
    weekId: string,
    data: { customQuestions: string[]; presetKeys: string[] },
  ): Promise<WeekSelfEnquiryQuestion[]> => {
    return apiService.client.put(`/api/weeks/${weekId}/self-enquiry-questions`, data);
  },

  getDailySelfEnquiry: async (weekId: string, date: string): Promise<DailySelfEnquiryView> => {
    return apiService.client.get(`/api/weeks/${weekId}/daily-self-enquiry/${date}`);
  },

  getSelfEnquiryJournal: async (): Promise<SelfEnquiryJournalEntry[]> => {
    return apiService.client.get('/api/weeks/self-enquiry-journal');
  },

  getSelfEnquiryJournalEntry: async (entryId: string): Promise<SelfEnquiryJournalEntry> => {
    return apiService.client.get(`/api/weeks/self-enquiry-journal/${entryId}`);
  },

  saveDailySelfEnquiry: async (weekId: string, data: { date: string; answerText: string }): Promise<{ id: string }> => {
    return apiService.client.put(`/api/weeks/${weekId}/daily-self-enquiry`, data);
  },

  addDailySelfEnquiryExtraQuestions: async (
    weekId: string,
    data: { date: string; presetKeys?: string[]; customQuestions?: string[] },
  ): Promise<Array<{ id: string; text: string; presetKey: string | null; sortOrder: number }>> => {
    return apiService.client.post(`/api/weeks/${weekId}/daily-self-enquiry/extra-questions`, data);
  },

  getArchiveReview: async (weekId: string): Promise<ArchiveReviewPayload> => {
    return apiService.client.get(`/api/weeks/${weekId}/archive-review`);
  },

  sendArchiveSummaryToTelegram: async (weekId: string): Promise<{ ok: true }> => {
    return apiService.client.post(`/api/weeks/${weekId}/send-archive-summary`);
  },
};

